import React, { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Autoplay } from 'swiper/modules';
import { Course } from '../../types/course';
import { useNarrowViewport } from '../../hooks/useNarrowViewport';
import SuggestedCourseCard from './SuggestedCourseCard';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';

interface SuggestedCoursesCarouselProps {
  courses: Course[];
  loading?: boolean;
  compact?: boolean;
}

const CarouselSkeleton = ({ compact = false }: { compact?: boolean }) => (
  <div className="suggested-courses-coverflow-triple-wrapper">
    <div className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2">
      {[1, 2].map((i) => (
        <div key={i} className="rounded-2xl border border-green-100 overflow-hidden animate-pulse bg-white">
          <div className="bg-gray-200 aspect-[16/9]" />
          <div className="-mt-5 ml-1 mr-6 mb-3 rounded-xl border border-green-100 bg-white p-4 space-y-2.5">
            <div className="h-5 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-10 bg-gray-200 rounded w-full mt-2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SuggestedCoursesCarousel: React.FC<SuggestedCoursesCarouselProps> = ({
  courses,
  loading = false,
  compact = false,
}) => {
  /** Swiper loop needs enough slides when 3 are visible at once */
  const slidesForLoop = useMemo(() => {
    if (courses.length <= 1) return courses;
    if (courses.length >= 6) return courses;
    const repeat = Math.ceil(6 / courses.length);
    return Array.from({ length: repeat }, () => courses).flat();
  }, [courses]);

  const enableLoop = slidesForLoop.length > 1;
  const isNarrow = useNarrowViewport();
  const isSingle = courses.length === 1;

  if (loading) return <CarouselSkeleton compact={compact} />;

  if (courses.length === 0) return null;

  // One course: skip Swiper/coverflow — grabCursor + transforms block button clicks
  if (isSingle) {
    return (
      <div className="suggested-courses-coverflow-triple-wrapper suggested-courses-single-course">
        <div className="mx-auto w-full max-w-[460px] px-1">
          <SuggestedCourseCard course={courses[0]} variant="carousel" compact={compact} />
        </div>
      </div>
    );
  }

  return (
    <div className="suggested-courses-coverflow-triple-wrapper">
      <Swiper
        modules={[EffectCoverflow, Pagination, Autoplay]}
        effect="coverflow"
        dir="rtl"
        grabCursor
        slidesPerView="auto"
        slidesPerGroup={1}
        spaceBetween={16}
        loop={enableLoop}
        loopAdditionalSlides={3}
        loopPreventsSliding={false}
        centeredSlides={false}
        pagination={{ clickable: true, dynamicBullets: true }}
        autoplay={
          enableLoop
            ? {
                delay: 1000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        preventClicks={false}
        preventClicksPropagation={false}
        speed={300}
        breakpoints={{
          0: {
            centeredSlides: true,
            spaceBetween: 12,
          },
          520: {
            centeredSlides: true,
            spaceBetween: 16,
          },
          900: {
            centeredSlides: true,
            spaceBetween: 24,
          },
        }}
        coverflowEffect={
          isNarrow
            ? { rotate: 0, stretch: 0, depth: 0, modifier: 1, slideShadows: false }
            : {
                rotate: compact ? 12 : 16,
                stretch: compact ? -6 : -10,
                depth: compact ? 100 : 150,
                modifier: compact ? 1.35 : 1.65,
                slideShadows: false,
              }
        }
        className="suggested-courses-coverflow-triple-swiper suggested-courses-coverflow-triple-swiper--courses"
      >
        {slidesForLoop.map((course, index) => (
          <SwiperSlide key={`${course.id}-${index}`}>
            <SuggestedCourseCard course={course} variant="carousel" compact={compact} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SuggestedCoursesCarousel;
